import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  useColorModeValue,
  useToast,
  Image,
  Icon,
  Progress,
  Tag,
  TagLabel,
  TagCloseButton,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { MdUpload, MdImage, MdClose, MdVideoLibrary, MdPlayArrow } from 'react-icons/md';
import Card from 'components/card/Card.js';
import RichTextEditor from 'components/fields/RichTextEditor';
import { createBlog, getBlog, updateBlog } from 'services/blogService';
import { getCategories } from 'services/categoryService';
import { uploadImagesBulk, uploadVideosBulk } from 'services/mediaService';
import { getCurrentUser } from 'services/authService';
import config from 'config';

export default function CreateBlog() {
  const { id } = useParams(); // Get blog ID if editing
  const isEditMode = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isVideoModalOpen, onOpen: onVideoModalOpen, onClose: onVideoModalClose } = useDisclosure();
  
  // Chakra Color Mode
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const brandColor = useColorModeValue('brand.500', 'white');
  const bg = useColorModeValue('gray.100', 'navy.700');
  const borderColor = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const progressBg = useColorModeValue('blue.50', 'blue.900');
  const progressBorder = useColorModeValue('blue.200', 'blue.700');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: [],
    status: 'draft',
  });
  
  const [images, setImages] = useState([]); // Array of { file, preview, url, uploading, progress }
  const [, setImageUploading] = useState(false);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [blogData, setBlogData] = useState(null); // Store blog data for category mapping

  // Fetch blog data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const fetchBlog = async () => {
        setLoading(true);
        try {
          const result = await getBlog(id);
          
          if (result.success && result.data) {
            const blog = result.data;
            setBlogData(blog); // Store blog data
            
            // Populate form with blog data
            setFormData({
              title: blog.title || '',
              content: blog.content || '',
              category: '', // Will be set after categories load
              tags: blog.tags || [],
              status: (blog.status || 'draft').toLowerCase(),
            });
            
            // Set images
            if (blog.images && blog.images.length > 0) {
              const imageObjects = blog.images.map((url) => ({
                id: Date.now() + Math.random() + url,
                file: null,
                preview: url.startsWith('http') ? url : `${config.api.baseURL}${url}`,
                url: url,
                uploading: false,
                progress: 100,
              }));
              setImages(imageObjects);
            }
            
            // Set video
            if (blog.video) {
              const videoUrl = blog.video.startsWith('http') ? blog.video : `${config.api.baseURL}${blog.video}`;
              setVideoUrl(blog.video);
              // Create a placeholder for video preview
              setFeaturedVideo({ name: 'video', url: videoUrl });
            }
          } else {
            toast({
              title: 'Error',
              description: result.error || 'Failed to load blog',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
            navigate('/admin/manage-blogs');
          }
        } catch (error) {
          console.error('Error fetching blog:', error);
          toast({
            title: 'Error',
            description: 'Failed to load blog. Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          navigate('/admin/manage-blogs');
        } finally {
          setLoading(false);
        }
      };
      
      fetchBlog();
    }
  }, [isEditMode, id, navigate, toast]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError(null);
      
      try {
        const result = await getCategories({ skip: 0, limit: 100 });
        
        if (result.success && result.data) {
          // Filter only active categories and map to select options
          const activeCategories = result.data
            .filter((cat) => cat.is_active)
            .map((cat) => ({
              value: cat.id, // Use category ID as value
              label: cat.name,
              slug: cat.slug,
            }));
          setCategories(activeCategories);
        } else {
          setCategoriesError(result.error || 'Failed to load categories');
          toast({
            title: 'Error',
            description: result.error || 'Failed to load categories',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error('Fetch categories error:', error);
        setCategoriesError('Failed to load categories. Please try again.');
        toast({
          title: 'Error',
          description: 'Failed to load categories. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [toast]);

  // Map blog category to category ID when both blog data and categories are available
  useEffect(() => {
    if (isEditMode && blogData && blogData.category && categories.length > 0) {
      const matchedCategory = categories.find(
        (cat) => cat.label.toLowerCase() === blogData.category.toLowerCase()
      );
      if (matchedCategory && formData.category !== matchedCategory.value) {
        setFormData((prev) => ({
          ...prev,
          category: matchedCategory.value,
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- formData.category intentionally excluded to avoid sync loop
  }, [isEditMode, blogData, categories]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // Handle rich text editor changes (content field)
  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
    // Clear error for content field
    if (errors.content) {
      setErrors((prev) => ({
        ...prev,
        content: '',
      }));
    }
  };

  // Handle multiple image uploads (bulk)
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max limit (20 images)
    if (files.length > 20) {
      toast({
        title: 'Too many images',
        description: 'Maximum 20 images allowed per upload',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate all files first
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        invalidFiles.push(`${file.name}: Unsupported format`);
      } else if (file.size > maxSize) {
        invalidFiles.push(`${file.name}: Exceeds 10MB limit`);
      } else {
        validFiles.push(file);
      }
    });

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      toast({
        title: 'Invalid files',
        description: invalidFiles.join(', '),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // Create previews for all valid files
    const previewPromises = validFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const imageId = Date.now() + Math.random() + file.name;
          resolve({
            id: imageId,
            file,
            preview: reader.result,
            url: null,
            uploading: true,
            progress: 0,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    // Wait for all previews to be ready, then add them and upload
    Promise.all(previewPromises).then((newImages) => {
      setImages((prev) => [...prev, ...newImages]);
      uploadImagesBulkWithProgress(validFiles, newImages);
    });

    // Reset input
    e.target.value = '';
  };

  // Upload images in bulk with progress tracking
  const uploadImagesBulkWithProgress = async (files, imageObjects) => {
    try {
      const imageIds = imageObjects.map((img) => img.id);
      setImageUploading(true);

      const result = await uploadImagesBulk(files, (progress) => {
        // Update progress for all uploading images
        setImages((prev) =>
          prev.map((img) =>
            imageIds.includes(img.id) && img.uploading
              ? { ...img, progress }
              : img
          )
        );
      });

      if (result.success && result.imageUrls && result.imageUrls.length > 0) {
        // Map uploaded URLs to image objects by index
        // imageObjects and files are in the same order
        const urlMap = new Map();
        result.imageUrls.forEach((url, index) => {
          if (index < imageObjects.length) {
            urlMap.set(imageObjects[index].id, url);
          }
        });

        // Update images with URLs
        setImages((prev) =>
          prev.map((img) => {
            if (imageIds.includes(img.id) && urlMap.has(img.id)) {
              return {
                ...img,
                url: urlMap.get(img.id),
                uploading: false,
                progress: 100,
              };
            }
            return img;
          })
        );

        toast({
          title: 'Success',
          description: `${result.count} image(s) uploaded successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Remove images that failed to upload (if any)
        if (result.count < imageIds.length) {
          const failedCount = imageIds.length - result.count;
          setImages((prev) =>
            prev.filter((img) => {
              if (imageIds.includes(img.id) && !img.url) {
                return false;
              }
              return true;
            })
          );
          toast({
            title: 'Partial upload',
            description: `${failedCount} image(s) failed to upload`,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        // Remove all failed images
        setImages((prev) => prev.filter((img) => !imageIds.includes(img.id)));
        toast({
          title: 'Upload failed',
          description: result.error || 'Failed to upload images',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Bulk image upload error:', error);
      const imageIds = imageObjects.map((img) => img.id);
      setImages((prev) => prev.filter((img) => !imageIds.includes(img.id)));
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred while uploading images',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setImageUploading(false);
    }
  };

  // Remove image
  const handleRemoveImage = (imageId) => {
    setImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  // Handle video upload (supports multiple videos via bulk upload)
  const handleVideoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check max limit (10 videos)
    if (files.length > 10) {
      toast({
        title: 'Too many videos',
        description: 'Maximum 10 videos allowed per upload',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate all files first
    const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
    const allowedExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const maxSize = 100 * 1024 * 1024; // 100MB
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!allowedTypes.includes(file.type) && !hasValidExtension) {
        invalidFiles.push(`${file.name}: Unsupported format`);
      } else if (file.size > maxSize) {
        invalidFiles.push(`${file.name}: Exceeds 100MB limit`);
      } else {
        validFiles.push(file);
      }
    });

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      toast({
        title: 'Invalid files',
        description: invalidFiles.join(', '),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    // For now, we'll use the first video (single video support)
    // TODO: Update to support multiple videos if needed
    const file = validFiles[0];
    setFeaturedVideo(file);

    // Upload video using bulk endpoint (even for single video)
    setVideoUploading(true);
    setVideoUploadProgress(0);

    try {
      const result = await uploadVideosBulk([file], (progress) => {
        setVideoUploadProgress(progress);
      });

      if (result.success && result.videoUrls && result.videoUrls.length > 0) {
        setVideoUrl(result.videoUrls[0]); // Use first video URL
        toast({
          title: 'Success',
          description: 'Video uploaded successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Upload failed',
          description: result.error || 'Failed to upload video',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setFeaturedVideo(null);
      }
    } catch (error) {
      console.error('Video upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred while uploading the video',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setFeaturedVideo(null);
    } finally {
      setVideoUploading(false);
      setVideoUploadProgress(0);
    }

    // Reset input
    e.target.value = '';
  };

  // Remove video
  const handleRemoveVideo = () => {
    setFeaturedVideo(null);
    setVideoUrl(null);
  };

  // Handle tag input
  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, newTag],
        }));
      }
      setTagInput('');
    }
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    // Remove HTML tags for validation (check if there's actual content)
    const contentText = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!contentText) {
      newErrors.content = 'Content is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Check if any images or video are still uploading
    const hasUploadingImages = images.some((img) => img.uploading);
    if (hasUploadingImages || videoUploading) {
      toast({
        title: 'Please wait',
        description: 'Media is still uploading. Please wait for it to complete.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Get current user for created_by
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.id) {
      toast({
        title: 'Error',
        description: 'User information not found. Please log in again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Find category name from categories array
    const selectedCategory = categories.find((cat) => cat.value === formData.category);
    if (!selectedCategory) {
      toast({
        title: 'Error',
        description: 'Selected category not found.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Get all uploaded image URLs
      const imageUrls = images
        .filter((img) => img.url)
        .map((img) => img.url);

      const blogData = {
        title: formData.title,
        content: formData.content,
        category: selectedCategory.label, // Send category name
        status: formData.status, // Will be converted to uppercase in service
        tags: formData.tags, // Already an array
        images: imageUrls.length > 0 ? imageUrls : undefined, // Send images array if available
        video: videoUrl || undefined, // Send video URL if available
      };

      // Only include created_by for new blogs
      if (!isEditMode) {
        blogData.created_by = currentUser.id;
      }

      let result;
      if (isEditMode) {
        // Update existing blog (full update using PUT)
        result = await updateBlog(id, blogData);
      } else {
        // Create new blog
        result = await createBlog(blogData);
      }

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || (isEditMode ? 'Blog updated successfully' : 'Blog created successfully'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        if (isEditMode) {
          // Navigate to manage blogs page after edit so user can see updated blog in the list
          setTimeout(() => {
            navigate('/admin/manage-blogs');
          }, 1500);
        } else {
          // Reset form for new blog
          setFormData({
            title: '',
            content: '',
            category: '',
            tags: [],
            status: 'draft',
          });
          setImages([]);
          setFeaturedVideo(null);
          setVideoUrl(null);
          setTagInput('');
          setErrors({});

          // Navigate to manage blogs page
          setTimeout(() => {
            navigate('/admin/manage-blogs');
          }, 1500);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to create blog',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: 'Error',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card mb='20px' p='24px'>
        <Text
          color={textColor}
          fontSize='2xl'
          fontWeight='700'
          mb='24px'>
          {isEditMode ? 'Edit Blog' : 'Create New Blog'}
        </Text>

        <form onSubmit={handleSubmit}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px' mb='20px'>
            {/* Title */}
            <FormControl isRequired isInvalid={!!errors.title} gridColumn={{ base: '1', md: '1 / 3' }}>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'>
                Blog Title<Text color='red.500'>*</Text>
              </FormLabel>
              <Input
                variant='main'
                fontSize='sm'
                placeholder='Enter blog title'
                name='title'
                value={formData.title}
                onChange={handleChange}
                mb='8px'
              />
              {errors.title && (
                <Text color='red.500' fontSize='xs' ms='4px'>
                  {errors.title}
                </Text>
              )}
            </FormControl>

            {/* Category */}
            <FormControl isRequired isInvalid={!!errors.category}>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'>
                Category<Text color='red.500'>*</Text>
              </FormLabel>
              <Select
                variant='main'
                fontSize='sm'
                placeholder={categoriesLoading ? 'Loading categories...' : categoriesError ? 'Error loading categories' : 'Select category'}
                name='category'
                value={formData.category}
                onChange={handleChange}
                mb='8px'
                isDisabled={categoriesLoading || categoriesError || categories.length === 0}>
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </Select>
              {errors.category && (
                <Text color='red.500' fontSize='xs' ms='4px'>
                  {errors.category}
                </Text>
              )}
              {categoriesError && !errors.category && (
                <Text color='orange.500' fontSize='xs' ms='4px'>
                  {categoriesError}
                </Text>
              )}
            </FormControl>

            {/* Status */}
            <FormControl>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'>
                Status
              </FormLabel>
              <Select
                variant='main'
                fontSize='sm'
                name='status'
                value={formData.status}
                onChange={handleChange}>
                <option value='draft'>Draft</option>
                <option value='published'>Published</option>
              </Select>
            </FormControl>
          </SimpleGrid>

          {/* Content */}
          <FormControl isRequired isInvalid={!!errors.content} mb='20px'>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              Content<Text color='red.500'>*</Text>
            </FormLabel>
            <RichTextEditor
              value={formData.content}
              onChange={handleContentChange}
              placeholder='Write your blog content here...'
            />
            {errors.content && (
              <Text color='red.500' fontSize='xs' ms='4px' mt='4px'>
                {errors.content}
              </Text>
            )}
          </FormControl>

          {/* Images */}
          <FormControl mb='20px'>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              Images
            </FormLabel>

            {/* Images Grid */}
            {images.length > 0 && (
              <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing='12px' mb='12px'>
                {images.map((img) => (
                  <Box key={img.id} position='relative' borderRadius='12px' overflow='hidden'>
                    <Image
                      src={img.preview}
                      alt='Image preview'
                      w='100%'
                      h='150px'
                      objectFit='cover'
                      opacity={img.uploading ? 0.6 : 1}
                    />
                    {img.uploading && (
                      <Box
                        position='absolute'
                        top='0'
                        left='0'
                        right='0'
                        bottom='0'
                        bg='rgba(0, 0, 0, 0.7)'
                        display='flex'
                        flexDirection='column'
                        align='center'
                        justify='center'
                        p='12px'>
                        <Progress
                          value={img.progress}
                          colorScheme='blue'
                          size='sm'
                          w='100%'
                          borderRadius='full'
                          mb='8px'
                          isAnimated
                          hasStripe
                        />
                        <Text fontSize='xs' color='white' fontWeight='500'>
                          {Math.round(img.progress)}%
                        </Text>
                      </Box>
                    )}
                    {!img.uploading && img.url && (
                      <Box
                        position='absolute'
                        bottom='4px'
                        left='4px'
                        bg='green.500'
                        color='white'
                        px='8px'
                        py='2px'
                        borderRadius='4px'
                        fontSize='xs'
                        fontWeight='500'>
                        ✓
                      </Box>
                    )}
                    <Button
                      position='absolute'
                      top='4px'
                      right='4px'
                      size='xs'
                      colorScheme='red'
                      onClick={() => handleRemoveImage(img.id)}
                      leftIcon={<Icon as={MdClose} />}
                      isDisabled={img.uploading}>
                      Remove
                    </Button>
                  </Box>
                ))}
              </SimpleGrid>
            )}

            {/* Upload Area */}
            <Flex
              align='center'
              justify='center'
              bg={bg}
              border='1px dashed'
              borderColor={borderColor}
              borderRadius='16px'
              p='40px'
              cursor='pointer'
              _hover={{ borderColor: brandColor }}
              position='relative'>
              <input
                type='file'
                accept='image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp'
                onChange={handleImageChange}
                multiple
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer',
                  zIndex: 1,
                }}
                disabled={images.some((img) => img.uploading)}
              />
              <Flex direction='column' align='center'>
                <Icon as={MdImage} w='48px' h='48px' color={brandColor} mb='12px' />
                <Text fontSize='sm' fontWeight='500' color={textColor} mb='4px'>
                  Click to upload images
                </Text>
                <Text fontSize='xs' color={textColorSecondary} textAlign='center'>
                  JPG, PNG, GIF, WEBP, BMP up to 10MB each
                  <br />
                  You can select multiple images
                </Text>
              </Flex>
            </Flex>
          </FormControl>

          {/* Featured Video */}
          <FormControl mb='20px'>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              Featured Video
            </FormLabel>
            
            {/* Video Upload Progress Bar */}
            {videoUploading && (
              <Box
                mb='12px'
                p='16px'
                bg={progressBg}
                borderRadius='12px'
                border='1px solid'
                borderColor={progressBorder}>
                <Flex align='center' justify='space-between' mb='8px'>
                  <Text fontSize='sm' fontWeight='500' color={textColor}>
                    Uploading video...
                  </Text>
                  <Text fontSize='sm' fontWeight='600' color={brandColor}>
                    {Math.round(videoUploadProgress)}%
                  </Text>
                </Flex>
                <Progress
                  value={videoUploadProgress}
                  colorScheme='blue'
                  size='lg'
                  borderRadius='full'
                  isAnimated
                  hasStripe
                />
                <Text fontSize='xs' color={textColorSecondary} mt='8px'>
                  This may take a while for large files...
                </Text>
              </Box>
            )}

            {/* Video Preview (after upload completes) */}
            {featuredVideo && !videoUploading && videoUrl && (
              <Box position='relative' mb='12px'>
                <Box
                  position='relative'
                  borderRadius='12px'
                  overflow='hidden'
                  bg='black'
                  cursor='pointer'
                  onClick={onVideoModalOpen}
                  _hover={{ opacity: 0.9 }}>
                  <Box
                    as='video'
                    src={videoUrl.startsWith('http') ? videoUrl : `${config.api.baseURL}${videoUrl}`}
                    maxH='400px'
                    w='100%'
                    pointerEvents='none'
                  />
                  <Box
                    position='absolute'
                    top='50%'
                    left='50%'
                    transform='translate(-50%, -50%)'
                    bg='rgba(0, 0, 0, 0.6)'
                    borderRadius='50%'
                    p='16px'
                    display='flex'
                    align='center'
                    justify='center'>
                    <Icon as={MdPlayArrow} w='40px' h='40px' color='white' />
                  </Box>
                </Box>
                <Button
                  position='absolute'
                  top='8px'
                  right='8px'
                  size='sm'
                  colorScheme='red'
                  onClick={handleRemoveVideo}
                  leftIcon={<Icon as={MdClose} />}>
                  Remove
                </Button>
                <Box
                  position='absolute'
                  bottom='8px'
                  left='8px'
                  bg='green.500'
                  color='white'
                  px='12px'
                  py='4px'
                  borderRadius='6px'
                  fontSize='xs'
                  fontWeight='500'>
                  ✓ Uploaded successfully
                </Box>
                <Button
                  position='absolute'
                  bottom='8px'
                  right='8px'
                  size='sm'
                  colorScheme='blue'
                  onClick={onVideoModalOpen}
                  leftIcon={<Icon as={MdPlayArrow} />}>
                  Play in Popup
                </Button>
              </Box>
            )}

            {/* Video Preview (during upload - using blob URL) */}
            {featuredVideo && videoUploading && (
              <Box position='relative' mb='12px' opacity={0.6}>
                <Box
                  as='video'
                  src={URL.createObjectURL(featuredVideo)}
                  controls
                  maxH='400px'
                  w='100%'
                  borderRadius='12px'
                  bg='black'
                />
              </Box>
            )}

            {/* Upload Area (when no video selected) */}
            {!featuredVideo && !videoUploading && (
              <Flex
                align='center'
                justify='center'
                bg={bg}
                border='1px dashed'
                borderColor={borderColor}
                borderRadius='16px'
                p='40px'
                cursor='pointer'
                _hover={{ borderColor: brandColor }}
                position='relative'>
                <Input
                  type='file'
                  accept='video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,.mp4,.webm,.ogg,.mov,.avi'
                  onChange={handleVideoChange}
                  position='absolute'
                  top='0'
                  left='0'
                  width='100%'
                  height='100%'
                  opacity='0'
                  cursor='pointer'
                  zIndex='1'
                  disabled={videoUploading}
                />
                <Flex direction='column' align='center'>
                  <Icon as={MdVideoLibrary} w='48px' h='48px' color={brandColor} mb='12px' />
                  <Text fontSize='sm' fontWeight='500' color={textColor} mb='4px'>
                    Click to upload video
                  </Text>
                  <Text fontSize='xs' color={textColorSecondary}>
                    MP4, WEBM, OGG, MOV, AVI up to 100MB
                  </Text>
                </Flex>
              </Flex>
            )}
          </FormControl>

          {/* Tags */}
          <FormControl mb='20px'>
            <FormLabel
              display='flex'
              ms='4px'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              Tags
            </FormLabel>
            <Input
              variant='main'
              fontSize='sm'
              placeholder='Type a tag and press Enter'
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              mb='12px'
            />
            {formData.tags.length > 0 && (
              <Flex wrap='wrap' gap='8px'>
                {formData.tags.map((tag) => (
                  <Tag
                    key={tag}
                    size='md'
                    borderRadius='full'
                    variant='solid'
                    colorScheme='brand'>
                    <TagLabel>{tag}</TagLabel>
                    <TagCloseButton onClick={() => handleRemoveTag(tag)} />
                  </Tag>
                ))}
              </Flex>
            )}
          </FormControl>

          {/* Upload Progress */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Progress value={uploadProgress} colorScheme='brand' mb='20px' />
          )}

          {/* Submit Buttons */}
          <Flex gap='12px' justify='flex-end'>
            <Button
              variant='light'
              fontWeight='500'
              onClick={() => navigate('/admin/manage-blogs')}
              disabled={loading}>
              Cancel
            </Button>
            <Button
              variant='brand'
              fontWeight='500'
              type='submit'
              isLoading={loading}
              loadingText='Creating...'
              leftIcon={<Icon as={MdUpload} />}>
              {formData.status === 'published' ? 'Publish Blog' : 'Save as Draft'}
            </Button>
          </Flex>
        </form>

        {/* Video Preview Modal */}
        <Modal isOpen={isVideoModalOpen} onClose={onVideoModalClose} size='xl' isCentered>
          <ModalOverlay />
          <ModalContent bg={bg}>
            <ModalHeader color={textColor}>Video Preview</ModalHeader>
            <ModalCloseButton color={textColor} />
            <ModalBody pb='24px'>
              {videoUrl && (
                <Box
                  as='video'
                  src={videoUrl.startsWith('http') ? videoUrl : `${config.api.baseURL}${videoUrl}`}
                  controls
                  w='100%'
                  maxH='70vh'
                  borderRadius='12px'
                  bg='black'
                />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Card>
    </Box>
  );
}

